<script setup lang="ts">
  import { Tooltip as ArkTooltip } from '@ark-ui/vue';
  import {
    tooltipContentClass,
    tooltipDescriptionClass,
    tooltipPositionerClass,
    tooltipTitleClass,
    tooltipTriggerResetClass,
  } from './Tooltip.css';
  import { computed, useSlots } from 'vue';
  import { GLOBAL_OVERLAYS_TARGET } from '../config/global-overlays-target';
  import { PlacementOptions } from '../utils/placement';

  export type TooltipProps = {
    label?: string;
    description?: string;
    openDelay?: number;
    interactive?: boolean;
    placement?: PlacementOptions;
    tooltipTriggerClass?: string;
    /** When true, the trigger wrapper stretches to 100% width. */
    triggerFullWidth?: boolean;
  };

  const props = withDefaults(defineProps<TooltipProps>(), {
    showArrow: false,
    interactive: true,
    openDelay: 100,
    placement: PlacementOptions.TOP,
    triggerFullWidth: false,
  });

  const slots = useSlots();
  const component = computed(() => slots.default!()[0]);

  defineOptions({
    inheritAttrs: false,
  });

  const hasDescription = computed(() => props.description !== undefined);

  const titleClasses = computed(() => {
    return hasDescription.value
      ? [tooltipTitleClass, tooltipDescriptionClass]
      : tooltipTitleClass;
  });

  const triggerStyle = computed(() =>
    props.triggerFullWidth ? { width: '100%' } : undefined,
  );
</script>

<template>
  <ArkTooltip.Root
    v-if="label"
    :interactive="interactive"
    :open-delay="openDelay"
    :positioning="{ placement: placement }"
  >
    <ArkTooltip.Trigger
      :class="[tooltipTriggerResetClass, tooltipTriggerClass]"
      :style="triggerStyle"
    >
      <component :is="component" v-bind="$props" />
    </ArkTooltip.Trigger>

    <Teleport defer :to="`.${GLOBAL_OVERLAYS_TARGET}`">
      <ArkTooltip.Positioner :class="tooltipPositionerClass">
        <ArkTooltip.Content :class="tooltipContentClass" v-bind="$attrs">
          <span :class="titleClasses"> {{ label }} </span>
          <p v-if="description" :class="tooltipDescriptionClass">
            {{ description }}
          </p>
        </ArkTooltip.Content>
      </ArkTooltip.Positioner>
    </Teleport>
  </ArkTooltip.Root>
  <slot v-else v-bind="$props" />
</template>
